import { ChevronDown, ChevronUp } from 'lucide-react';
import { IconButton, cn } from '@wundo/ui';
import { attentionOf } from '@/components/conversationReview/attentionOf.ts';
import { SEVERITY_MARK_CLASS } from '@/components/conversationReview/severity.ts';
import { FindingNote } from './FindingNote.tsx';
import { isUnplaced } from './isUnplaced.ts';
import type { PlacedFinding } from './placeFindings.ts';

export interface FindingsBarProps {
  /** In the order the arrows walk them: the ones under no message first, then down the transcript. */
  findings: PlacedFinding[];
  current: string | null;
  onSelect: (key: string) => void;
}

/**
 * The open findings of the transcript being read, walked one at a time. A
 * finding that sits under no message is shown here instead, so it is never lost.
 */
export function FindingsBar({ findings, current, onSelect }: FindingsBarProps) {
  const attention = attentionOf(findings.map((placed) => placed.finding.severity));
  if (!attention) return null;

  const index = findings.findIndex((placed) => placed.key === current);
  const unplaced = findings.filter(isUnplaced);
  const total = findings.length;

  return (
    <div data-ui='findings-bar' className='border-t border-line bg-fill-faint text-xs'>
      <div className='flex items-center gap-2 py-1 pr-2 pl-4'>
        <span
          aria-hidden='true'
          className={cn(
            'size-2 shrink-0 rounded-full',
            SEVERITY_MARK_CLASS[attention.severity],
          )}
        />
        <span className='font-semibold text-ink'>
          {total} open {total === 1 ? 'finding' : 'findings'}
        </span>
        <span className='flex-1' />
        {index >= 0 && (
          <span className='text-ink-muted tabular-nums' aria-live='polite'>
            {index + 1} of {total}
          </span>
        )}
        <IconButton
          size='sm'
          aria-label='Previous finding'
          disabled={index <= 0}
          onClick={() => onSelect(findings[index - 1].key)}
        >
          <ChevronUp className='size-3.5' aria-hidden='true' />
        </IconButton>
        <IconButton
          size='sm'
          aria-label='Next finding'
          disabled={index >= total - 1}
          onClick={() => onSelect(findings[index + 1].key)}
        >
          <ChevronDown className='size-3.5' aria-hidden='true' />
        </IconButton>
      </div>

      {unplaced.length > 0 && (
        <div className='flex flex-col gap-1.5 px-4 pb-2'>
          {unplaced.map((placed) => (
            <FindingNote
              key={placed.key}
              placed={placed}
              author='wundo'
              className='ml-0 max-w-none'
            />
          ))}
        </div>
      )}
    </div>
  );
}
